import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

// Available languages
export const LANGUAGES = {
  en: { code: 'en', name: 'English', nativeName: 'English' },
  es: { code: 'es', name: 'Spanish', nativeName: 'Español' },
  fr: { code: 'fr', name: 'French', nativeName: 'Français' },
  de: { code: 'de', name: 'German', nativeName: 'Deutsch' }
};

// Default language
const DEFAULT_LANGUAGE = 'en';

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Initialize from localStorage or browser preference
    const savedLanguage = localStorage.getItem('va_sprouts_language');
    if (savedLanguage && LANGUAGES[savedLanguage]) {
      return savedLanguage;
    }

    // Try to detect browser language
    const browserLang = navigator.language.split('-')[0];
    if (LANGUAGES[browserLang]) {
      return browserLang;
    }

    return DEFAULT_LANGUAGE;
  });

  // Update localStorage when language changes
  useEffect(() => {
    localStorage.setItem('va_sprouts_language', currentLanguage);
    // Update document lang attribute for accessibility
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  const changeLanguage = (languageCode) => {
    if (LANGUAGES[languageCode]) {
      setCurrentLanguage(languageCode);
    } else {
      console.warn(`Language ${languageCode} not supported. Falling back to ${DEFAULT_LANGUAGE}`);
      setCurrentLanguage(DEFAULT_LANGUAGE);
    }
  };

  const value = {
    currentLanguage,
    changeLanguage,
    languages: LANGUAGES,
    currentLanguageData: LANGUAGES[currentLanguage]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
